// Libraries
import React, { useEffect, useRef, useState, useContext, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

// Material UI Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";

// Local Files
import AiChatSelection from "./aiChatSelection.js";
import ChatMessage from "./chatMessage.js";
import SuggestedPrompts from './suggestedPrompts';
import AiPromptBar from "./aiPromptBar.js";
import ModelSelector from "./modelSelector.js";
import ServiceSelector from "./serviceSelector.js";
import userApi from "../../server/usersApi";
import completionsApi from "../../server/completionsApi.js";
import { generateConfig } from "./aiChatConfigHelper";
import { copyToClipboard } from "../reusableComponent/helpers.js";
import { userProfileContext } from "../../lib/UserProvider";
import {
  findByValue,
  convertToTemperatureValue,
  findFirstTeamByUseCase,
} from "./helpers.js";
import {
  extractArticleData,
  normaliseAssistantMessages
} from "../reusableComponent/helpers.js";
import {
  teamServiceTypeMapping,
  languages,
  lengths,
  temperatures,
  tones,
  teams,
  serviceTypes,
  models,
  publications,
  articleTypes,
  empty,
  booleans,
  getServiceTypes
} from "../../definitions";

// Material UI Icons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ReplayIcon from "@mui/icons-material/Replay";

const CircleIconButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
  borderRadius: "50%",
  border: `1px solid ${theme.palette.primary.main}`,
  "&:hover": {
    background: "transparent",
  },
}));

// small helpers for URL param parsing/validation
const numberFromParam = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// coerce "value" | { value } | other -> string (or fallback)
const toValueString = (maybe, fallback) => {
  if (typeof maybe === "string") return maybe;
  if (maybe && typeof maybe.value === "string") return maybe.value;
  return fallback;
};

// short key map for compact URLs (backwards-compatible parser below)
const Q = {
  model: "m",
  service: "s",
  team: "tm",
  tone: "tn",
  length: "ln",
  fullscreen: "fs",
  writingStyle: "ws",
  temperature: "tp",
  language: "lg",
  systemMessage: "sm",
};

// Services that require JSON article output (moved up so it's stable)
const EDITORIAL_ARTICLE_SERVICES = [
  "Article Adapter",
  "Article Composer",
  "Article Rewriter",
  "Commenting Article Generator",
  "Long-Form Article Composer",
  "News Wire to Article",
  "Press Release to Article",
  "Product Review",
  "Public Notices Article Generator",
  "Public Notices Article Summary",
  "Weather Article Composer (AccuWeather)",
  "Amazon Affiliate Offer Article Generator",
  "AWIN Affiliate Offer Article Generator",
  "Skimlinks Affiliate Offer Article Generator",
  "Skimlinks Product Article Generator",
];

const isEditorialTeam = (team) => {
  const raw = typeof team === "string" ? team : (team && typeof team.value === "string") ? team.value : "";
  if (!raw) return false;
  const value = raw.trim().toLowerCase();
  return value === "editorial" || value === "commercial editorial";
};

const AiChat = () => {
  const navigate = useNavigate();
  const userProfile = useContext(userProfileContext);
  const { id } = useParams();
  const chatBoxRef = useRef(null);
  const latestMessageRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const didInitFromQuery = useRef(false);

  const chatServiceType = useMemo(() => (
    getServiceTypes(serviceTypes, { specificServiceType: "Chat" })[0]?.value || serviceTypes[0].value
  ), []);

  const [input, setInput] = useState({
    id: id || null,
    useQueue: booleans[0].value, // keep for back-compat
    deliveryMode: 'queue',      // explicit mode ('direct' | 'queue' | 'stream')
    options: {
      max_length: lengths[3].value,
      messages: [],
      model: models[0].value,
      temperature: temperatures[5].value,
      user: userProfile?.user_id || null,
      suggestionsEnabled: true,
      response_format: { type: "text" },
    },
    settings: {
      articleTone: tones[5].value,
      language: languages[0].value,
      serviceType: chatServiceType,
      writingStyle: empty.value,
      team: teams[0].value,
    },
    user_id: userProfile?.user_id || null,
    user_name: userProfile?.name?.split(' ')[0] || null,
    isTemporaryChat: false,
    articleData: {
      title: "",
      body: "",
      url: "",
      wordCount: "",
      leadText: "",
      metaTitle: "",
      metaDescription: "",
      newsLettersAndReferrersHeadline: "",
      tags: []
    }
  });

  // Refs to avoid stale-closure bugs when sending immediately after state changes
  const inputRef = useRef(input);
  const messagesRef = useRef(input.options.messages);
  const sessionKeyRef = useRef(Date.now()); // bumps on New Chat to force fresh closures

  useEffect(() => { inputRef.current = input; });
  useEffect(() => { messagesRef.current = input.options.messages; });

  const [showWatermark, setShowWatermark] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [aiChatSelectionVisible, setAiChatSelectionVisible] = useState(true);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState({ content: "", severity: "success" });
  const [queryApplied, setQueryApplied] = useState(false);
  const [exposeParams, setExposeParams] = useState(false);

  // let AdvancedOptions know what came from the link so it can offer "save these as defaults?"
  const linkHydrateRef = useRef(null);

  // Parse query params (supports both old long keys and new short keys)
  useEffect(() => {
    if (didInitFromQuery.current) return;

    // long keys (back-compat)
    const qsModel = searchParams.get("model");
    const qsService = searchParams.get("service");
    const qsTeam = searchParams.get("team");
    const qsTone = searchParams.get("tone");
    const qsLength = searchParams.get("length") || searchParams.get("len");
    const qsFullscreen = searchParams.get("fullscreenMode");
    const qsWritingStyle = searchParams.get("writingStyle");
    const qsTemp = searchParams.get("temperature");
    const qsLang = searchParams.get("language");
    const qsSys = searchParams.get("system");

    // short keys (preferred)
    const sModel = searchParams.get(Q.model);
    const sService = searchParams.get(Q.service);
    const sTeam = searchParams.get(Q.team);
    const sTone = searchParams.get(Q.tone);
    const sLength = searchParams.get(Q.length);
    const sFullscreen = searchParams.get(Q.fullscreen);
    const sWritingStyle = searchParams.get(Q.writingStyle);
    const sTemp = searchParams.get(Q.temperature);
    const sLang = searchParams.get(Q.language);
    const sSys = searchParams.get(Q.systemMessage);

    const getFirst = (...vals) => vals.find(v => v !== null && v !== undefined && v !== "");

    const model = getFirst(sModel, qsModel);
    const service = getFirst(sService, qsService);
    const team = getFirst(sTeam, qsTeam);
    const tone = getFirst(sTone, qsTone);
    const lengthParam = getFirst(sLength, qsLength);
    const fullscreen = getFirst(sFullscreen, qsFullscreen);
    const writingStyle = getFirst(sWritingStyle, qsWritingStyle);
    const temperatureParam = getFirst(sTemp, qsTemp);
    const languageParam = getFirst(sLang, qsLang);
    const systemMessage = getFirst(sSys, qsSys);

    const anyQP = !!(model || service || team || tone || lengthParam || fullscreen || writingStyle || temperatureParam || languageParam || systemMessage);

    // remember what came in so AdvancedOptions can offer "save?"
    linkHydrateRef.current = anyQP ? {
      model,
      service,
      team,
      tone,
      length: lengthParam,
      fullscreen,
      writingStyle,
      temperature: temperatureParam,
      language: languageParam,
      systemMessage,
    } : null;

    setInput(prev => {
      const next = { ...prev };

      if (model) next.options.model = toValueString(findByValue(models, model), prev.options.model);
      if (service) next.settings.serviceType = toValueString(findByValue(serviceTypes, service), prev.settings.serviceType);
      if (team) next.settings.team = toValueString(findByValue(teams, team), prev.settings.team);
      if (tone) next.settings.articleTone = toValueString(findByValue(tones, tone), prev.settings.articleTone);
      if (writingStyle) next.settings.writingStyle = toValueString(findByValue(publications, writingStyle), prev.settings.writingStyle);
      if (languageParam) next.settings.language = toValueString(findByValue(languages, languageParam), prev.settings.language);

      if (lengthParam) {
        const parsed = numberFromParam(lengthParam);
        const isValid = lengths.some(x => Number(x.value) === parsed);
        if (isValid) next.options.max_length = parsed;
      }

      if (temperatureParam !== null && temperatureParam !== undefined) {
        const asNumber = Number(temperatureParam);
        if (!Number.isNaN(asNumber) && asNumber >= 0 && asNumber <= 1) {
          next.options.temperature = asNumber;
        } else {
          const found = findByValue(temperatures, temperatureParam);
          if (found !== null && found !== undefined) next.options.temperature = Number(toValueString(found, prev.options.temperature));
        }
      }

      if (systemMessage && typeof systemMessage === "string") {
        const sysIndex = next.options.messages.findIndex(m => m.role === "system");
        if (sysIndex >= 0) next.options.messages[sysIndex].content = systemMessage;
        else next.options.messages.unshift({ role: "system", content: systemMessage });
      }

      return next;
    });

    setAiChatSelectionVisible(fullscreen !== "true");
    setQueryApplied(anyQP);

    didInitFromQuery.current = true;
    // eslint-disable-next-line
  }, []);

  // Automatically update response_format when team or serviceType changes
  useEffect(() => {
    setInput((prev) => {
      const isEditorial = isEditorialTeam(prev.settings.team);
      const isArticleService = EDITORIAL_ARTICLE_SERVICES.includes(prev.settings.serviceType);
      const response_format = isEditorial && isArticleService ? { type: "json_object" } : { type: "text" };
      if (prev.options.response_format?.type === response_format.type) return prev; // no-op
      return { ...prev, options: { ...prev.options, response_format } };
    });
  }, [input.settings.team, input.settings.serviceType]);

  // Keep URL in sync when user toggles "Show params"
  useEffect(() => {
    if (!didInitFromQuery.current || !exposeParams) return;

    const next = new URLSearchParams(searchParams);

    // short keys
    if (input.options.model) next.set(Q.model, input.options.model);
    if (input.settings.serviceType) next.set(Q.service, input.settings.serviceType);
    if (input.settings.team) next.set(Q.team, input.settings.team);
    if (input.settings.articleTone) next.set(Q.tone, input.settings.articleTone);
    if (input.settings.writingStyle) next.set(Q.writingStyle, input.settings.writingStyle);
    if (input.settings.language) next.set(Q.language, input.settings.language);

    if (input.options.max_length) next.set(Q.length, String(input.options.max_length)); else next.delete(Q.length);
    if (typeof input.options.temperature === "number") next.set(Q.temperature, String(input.options.temperature)); else next.delete(Q.temperature);

    if (!aiChatSelectionVisible) next.set(Q.fullscreen, "true"); else next.delete(Q.fullscreen);

    // keep system message out of live URL by default (shorter/cleaner)
    next.delete(Q.systemMessage);

    // remove old long keys to avoid bloat
    ["model", "service", "team", "tone", "length", "len", "fullscreenMode", "writingStyle", "temperature", "language", "system"].forEach(k => next.delete(k));

    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [
    input.options.model,
    input.settings.serviceType,
    input.settings.team,
    input.settings.articleTone,
    input.settings.writingStyle,
    input.settings.language,
    input.options.max_length,
    input.options.temperature,
    aiChatSelectionVisible,
    exposeParams
  ]);

  const toggleFullscreenMode = () => setAiChatSelectionVisible(v => !v);

  const fetchSavedChat = useCallback(async () => {
    // bail out early if either is missing
    if (!id || !userProfile?.user_id) return;

    try {
      const res = await userApi.fetchUserMessageById(userProfile.user_id, id);
      const obj = Array.isArray(res) ? res[0] : res;
      if (!obj || typeof obj !== 'object') return;

      const msgs =
        Array.isArray(obj?.options?.messages) ? obj.options.messages :
          Array.isArray(obj?.messages) ? obj.messages :
            Array.isArray(obj?.messages_array?.messages) ? obj.messages_array.messages :
              Array.isArray(obj?.messages_array) ? obj.messages_array :
                [];

      // build normalised config
      const config = {
        ...obj,
        options: {
          ...(obj.options || {}),
          messages: msgs,
        },
      };

      // enforce single source of truth:
      delete config.messages;
      delete config.messages_array;

      const { options, settings } = config;
      const rawMessages = options.messages;
      const responseFormat = options?.response_format?.type ?? 'text';

      const { messages: normalisedMessages, articleData: extractedArticleData } =
        normaliseAssistantMessages(rawMessages, responseFormat);

      const selectedTeam = findFirstTeamByUseCase(settings.serviceType, teamServiceTypeMapping);

      setInput((prev) => ({
        ...prev,
        id: config.id,
        options: {
          ...prev.options,
          max_length: options.max_length,
          messages: normalisedMessages,
          model: options.model,
          temperature: settings?.articleCreativity !== undefined
            ? convertToTemperatureValue(temperatures, settings.articleCreativity)
            : options?.temperature !== undefined
              ? convertToTemperatureValue(temperatures, options.temperature)
              : prev.options.temperature,
          user: userProfile?.user_id || null,
        },
        settings: {
          ...prev.settings,
          articleTone: toValueString(settings.articleTone, prev.settings.articleTone),
          language: toValueString(settings.language, prev.settings.language),
          serviceType: toValueString(settings.serviceType, prev.settings.serviceType),
          writingStyle: toValueString(settings.writingStyle, prev.settings.writingStyle),
          team: toValueString(selectedTeam ?? settings.team, prev.settings.team),
        },
        user_id: userProfile?.user_id || null,
        ...(extractedArticleData && { articleData: extractedArticleData }),
        deliveryMode: config?.deliveryMode || prev.deliveryMode,
        useQueue: typeof config?.useQueue === 'boolean' ? config.useQueue : prev.useQueue,
      }));
    } catch (error) {
      console.error("Error fetching chat data:", error);
    }
  }, [id, userProfile?.user_id]);

  useEffect(() => { if (id) fetchSavedChat(); }, [id, fetchSavedChat]);

  const navigateToPushToCue = (combinedInput) => {
    navigate("/push-to-cms", { state: combinedInput });
  };

  const processAndPushArticleData = (chatContents, responseFormat) => {
    const articleData = input?.articleData?.title && input?.articleData?.body
      ? input.articleData
      : extractArticleData(chatContents, responseFormat);

    if (!articleData?.title || !articleData?.body) {
      setSnackbarMessage({
        content: "This article is missing a title or body. It will still be pushed to CMS.",
        severity: "warning",
      });
    }

    const updatedOptions = {
      model: input?.options?.model ?? null,
      temperature: input?.options?.temperature ?? null,
      max_length: input?.options?.max_length ?? null,
      messages: input?.options?.messages ?? null,
    };

    const updatedSettings = {
      serviceType: input?.settings?.serviceType ?? null,
      language: input?.settings?.language ?? null,
      articleTone: input?.settings?.articleTone ?? null,
    };

    const updatedCmsData = {
      ...(input?.settings?.writingStyle && {
        cms: input.settings.writingStyle,
        cmsEntries: [input.settings.writingStyle],
      }),
      cmsConfig: {},
      cmsContentType: articleTypes[15].value,
      cmsPublicationStatus: "draft",
    };

    const combinedInput = {
      articleData,
      options: updatedOptions,
      settings: updatedSettings,
      cmsData: updatedCmsData,
    };

    navigateToPushToCue(combinedInput);
  };

  const handleAction = (actionType, chat) => {
    if (actionType === "copy" && chat.role === "assistant") {
      const messageToCopy = chat.content;
      copyToClipboard(messageToCopy)
        .then(() => setSnackbarMessage({ content: "Message copied to clipboard.", severity: "success" }))
        .catch(() => setSnackbarMessage({ content: "Error copying message.", severity: "error" }));
    } else if (actionType === "sendToCue" && chat.role === "assistant") {
      const chatContents = chat.content;
      let responseFormat = "text";
      if (typeof chatContents === "string" && chatContents.trim().startsWith("{") && chatContents.trim().endsWith("}")) {
        try { JSON.parse(chatContents); responseFormat = "json_object"; } catch {/* ignore */ }
      }
      processAndPushArticleData(chatContents, responseFormat);
    }
  };

  // Smooth scroll to the latest real message
  useEffect(() => {
    if (latestMessageRef.current && chatBoxRef.current) {
      latestMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    if (input.options.messages.length === 0) {
      setShowWatermark(true);
      setShowRegenerate(false);
    } else {
      setShowWatermark(false);
      const lastMessage = input.options.messages[input.options.messages.length - 1];
      setShowRegenerate(lastMessage?.role === "assistant");
    }
  }, [input.options.messages]);

  const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

  const handleRegenerateResponse = () => {
    const msgs = messagesRef.current;
    if (!msgs.length) return;

    const updatedMessages = msgs.slice(0, -1);

    setInput((prev) => ({
      ...prev,
      options: { ...prev.options, messages: updatedMessages },
    }));

    const hasAssistantMessages = updatedMessages.some(chat => chat.role === "assistant");
    setShowRegenerate(hasAssistantMessages);

    const base = inputRef.current;
    const regeneratedConfig = {
      ...base,
      options: {
        ...base.options,
        max_length: base?.options?.max_length || "5000",
        messages: updatedMessages,
        model: base?.options?.model || "latest",
        temperature: base?.options?.temperature ?? 0.5,
        user: base?.options?.user || (userProfile?.user_id || null),
      }
    };

    if (regeneratedConfig?.messages) delete regeneratedConfig.messages;

    handleSendMessage(regeneratedConfig);
  };

  const enqueueMessage = async (config) => {
    const BASE_POLL_MS = 2000;   // align with backend cadence
    const TIMEOUT_MS = 240000; // hard cap (same as before)

    const parseMaybeJson = (out) => {
      if (out == null) return null;
      if (typeof out === 'object') return out;
      try { return JSON.parse(out); } catch { return out; }
    };

    // Prefer backend-provided delay; fall back to base, add tiny jitter
    const delayFromHints = (task, headers) => {
      const headerRetryAfter =
        headers && (Number(headers['retry-after']) * 1000 || null);

      const serverMs =
        (task && (task.retryAfterMs ?? task.nextCheckInMs ?? task.pollMs)) ??
        headerRetryAfter ??
        null;

      const raw = serverMs ?? BASE_POLL_MS;
      const jitter = Math.floor((Math.random() * 2 - 1) * 150); // ±150ms
      return Math.max(250, raw + jitter);
    };

    const response = await completionsApi.enqueueMessageToCompletionsAPI(config);
    if (!(response?.status === 202 && response?.id)) {
      throw new Error(response?.response?.data?.message || response?.message || 'Failed to enqueue');
    }

    const taskId = response.id;
    const start = Date.now();
    let pollTimer = null;
    let hardStopTimer = null;

    const schedule = (ms, fn) => { pollTimer = setTimeout(fn, ms); };
    const clear = () => { if (pollTimer) clearTimeout(pollTimer); pollTimer = null; };

    const finish = (task) => {
      clear();
      if (hardStopTimer) { clearTimeout(hardStopTimer); hardStopTimer = null; }
      try {
        const fetchedChat = parseMaybeJson(task?.output);
        handleFetchedChat(fetchedChat);
      } catch (e) {
        console.error('handleFetchedChat error:', e?.message || e);
      }
    };

    const poll = async () => {
      if (Date.now() - start >= TIMEOUT_MS) {
        clear();
        console.warn('Polling timed out waiting for completion.');
        return;
      }

      try {
        // Support either { task, headers } or task directly
        const resp = await completionsApi.checkTaskStatusInCompletionsQueue(taskId);
        const task = resp?.task ?? resp;
        const hdrs = resp?.headers ?? resp?.response?.headers ?? null;

        if (!task) {
          // nothing returned, try again on base cadence (with header hints if any)
          const nextMs = delayFromHints(null, hdrs);
          return schedule(nextMs, poll);
        }

        const taskStatus = (task.message ?? '').toString();
        if (taskStatus === 'Completed' || taskStatus === 'Failed') {
          return finish(task);
        }

        // Still running → follow server hint or base cadence
        const nextMs = delayFromHints(task, hdrs);
        schedule(nextMs, poll);
      } catch (err) {
        const status = err?.response?.status;
        const hdrs = err?.response?.headers || null;

        // If the task is gone after completion (server deleted it), stop polling.
        if (status === 404 || status === 410) {
          clear();
          if (hardStopTimer) { clearTimeout(hardStopTimer); hardStopTimer = null; }
          console.warn(`Task ${taskId} no longer exists on the server (status ${status}). Stopping polling.`);
          return;
        }

        // Backoff using Retry-After when available (e.g., 429/503)
        const nextMs = delayFromHints(null, hdrs);
        schedule(nextMs, poll);
      }
    };

    // Kick off first poll slightly sooner than base cadence
    schedule(Math.max(0, BASE_POLL_MS / 2), poll);

    // Hard stop guard (mirrors your previous clearInterval timeout)
    hardStopTimer = setTimeout(clear, TIMEOUT_MS);
  };

  // Streaming path (keeps existing behaviour when not streaming)
  const sendStreamMessage = async (config) => {
    const runKey = sessionKeyRef.current;
    let buffer = "";

    setLoadingMessage({ role: "assistant", content: "Mantis Scribe is Typing..." });

    try {
      await completionsApi.streamMessageToCompletionsAPI(
        { ...config, options: { ...config.options, stream: true } },
        {
          onChunk: (t) => {
            if (runKey !== sessionKeyRef.current) return;
            if (typeof t !== "string" || !t) return;
            buffer += t;
            setLoadingMessage({ role: "assistant", content: buffer });
          },
          onDone: () => {
            if (runKey !== sessionKeyRef.current) return;
            setLoadingMessage(null);
            if (buffer) {
              setInput((prev) => ({
                ...prev,
                options: { ...prev.options, messages: [...(prev.options.messages || []), { role: "assistant", content: buffer }] },
              }));
            }
          },
          onError: (e) => {
            if (runKey !== sessionKeyRef.current) return;
            setLoadingMessage(null);
            const msg = e?.message || "Streaming error. Please try again.";
            setInput((prev) => ({
              ...prev,
              options: { ...prev.options, messages: [...(prev.options.messages || []), { role: "assistant", content: msg }] },
            }));
          }
        }
      );
    } catch (e) {
      setLoadingMessage(null);
      const msg = e?.message || "Streaming error. Please try again.";
      setInput((prev) => ({
        ...prev,
        options: { ...prev.options, messages: [...(prev.options.messages || []), { role: "assistant", content: msg }] },
      }));
    }
  };

  const handleSendMessage = async (config) => {
    const { settings, options } = config;
    const { messages } = options;

    setLoadingMessage({ role: "assistant", content: "Mantis Scribe is Typing..." });

    if (!messages || !messages.length) {
      setLoadingMessage(null);
      throw new Error("Please provide a message.");
    }

    try {
      // Derive mode (back-compat): prefer deliveryMode, then useQueue
      const mode =
        inputRef.current.deliveryMode ||
        (inputRef.current.useQueue ? 'queue' : 'direct');

      if (mode === 'stream') {
        await sendStreamMessage(config);
        return;
      }

      const shouldEnqueue = inputRef.current.useQueue || mode === 'queue'; // Ensure boolean value

      if (!settings.serviceType.includes("Article") || !shouldEnqueue) {
        const response = await completionsApi.sendMessageToCompletionsAPI(config);
        if (response.status === 200) {
          handleFetchedChat(response);
        } else if (response?.response?.status === 504) {
          await enqueueMessage(config);
        } else {
          throw new Error(`${response?.response?.data?.message ? response.response.data.message : response.message}`);
        }
      } else {
        await enqueueMessage(config);
      }

    } catch (error) {
      setLoadingMessage(null);
      const errorMessage = `${error?.toString() ?? "Error: An error occurred. Please try again."}`;
      setInput((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          messages: [...(prev.options.messages || []), { role: "assistant", content: errorMessage }]
        }
      }));
    }
  };

  const handleFetchedChat = (fetchedChat) => {
    if (fetchedChat !== null) {
      const { message = "", suggestions = [], id: returnedId } = fetchedChat;
      const messageResponse = typeof message === "string" ? message : "";
      setLoadingMessage(null);

      const { messages, articleData } = normaliseAssistantMessages(
        [{ role: "assistant", content: messageResponse }],
        inputRef.current.options?.response_format?.type || "text"
      );

      setInput((prev) => {
        const next = {
          ...prev,
          id: returnedId || prev?.id || null,
          articleData: articleData ? { ...prev.articleData, ...articleData } : prev.articleData,
          options: { ...prev.options, messages: [...(prev.options.messages || []), ...messages] },
        };

        // update URL path with chat id when backend assigns it
        if (returnedId && returnedId !== prev?.id) {
          const params = new URLSearchParams(searchParams);
          navigate(`/ai-chat/${returnedId}?${params.toString()}`, { replace: true });
        }
        return next;
      });

      if (inputRef.current?.options?.suggestionsEnabled && Array.isArray(suggestions)) {
        setSuggestedPrompts(suggestions);
      }
    } else {
      setLoadingMessage(null);
      const errorMessage = `An error occurred. Please try again.`;
      setInput((prev) => ({
        ...prev,
        options: { ...prev.options, messages: [...(prev.options.messages || []), { role: "assistant", content: errorMessage }] },
      }));
    }
  };

  // Clear id and route on New Chat so the next prompt can't attach to old session
  const handleStartNewChat = () => {
    const params = new URLSearchParams(searchParams);
    navigate(`/ai-chat${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });

    const systemMessages = inputRef.current.options.messages.filter((m) => m.role === "system");

    setSuggestedPrompts([]);
    setLoadingMessage(null);
    setShowRegenerate(false);

    // Bump session key to bust any stale closures
    sessionKeyRef.current = Date.now();

    setInput((prev) => ({
      ...prev,
      id: null,
      options: { ...prev.options, messages: [...systemMessages] },
    }));
  };

  const handleModelChange = (newModel) => {
    setInput((prev) => ({ ...prev, options: { ...prev.options, model: toValueString(newModel, prev.options.model) } }));
  };

  const handleServiceChange = (newServiceType) => {
    setInput((prev) => ({ ...prev, settings: { ...prev.settings, serviceType: toValueString(newServiceType, prev.settings.serviceType) } }));
  };

  // Build a shareable short-URL with current setup (does NOT change your address bar)
  const buildShareUrl = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    params.set(Q.model, inputRef.current.options.model);
    params.set(Q.service, inputRef.current.settings.serviceType);
    params.set(Q.team, inputRef.current.settings.team);
    if (inputRef.current.settings.articleTone) params.set(Q.tone, inputRef.current.settings.articleTone);
    if (inputRef.current.settings.writingStyle) params.set(Q.writingStyle, inputRef.current.settings.writingStyle);
    if (inputRef.current.settings.language) params.set(Q.language, inputRef.current.settings.language);
    if (typeof inputRef.current.options.temperature === "number") params.set(Q.temperature, String(inputRef.current.options.temperature));
    if (inputRef.current.options.max_length) params.set(Q.length, String(inputRef.current.options.max_length));
    if (!aiChatSelectionVisible) params.set(Q.fullscreen, "true");

    const sys = inputRef.current.options.messages.find(m => m.role === "system")?.content;
    if (sys && sys.trim()) params.set(Q.systemMessage, sys.trim());

    url.pathname = inputRef.current.id ? `/ai-chat/${inputRef.current.id}` : `/ai-chat`;
    url.search = params.toString();
    return url.toString();
  };

  const handleShareSetup = async () => {
    try { await copyToClipboard(buildShareUrl()); setSnackbarMessage({ content: "Shareable link copied to clipboard.", severity: "success" }); }
    catch { setSnackbarMessage({ content: "Could not copy link.", severity: "error" }); }
  };

  const handleUserSubmit = useCallback(({ message, tool_choice }) => {
    if (!message || typeof message !== "string") {
      console.error("Invalid message passed to onSend.");
      return;
    }

    // Always derive from latest messages (fixes 'New Chat' stale history bug)
    const baseMessages = messagesRef.current || [];
    const updatedMessages = [...baseMessages, { role: "user", content: message }];

    setInput((prev) => ({
      ...prev,
      options: { ...prev.options, messages: updatedMessages }
    }));

    const current = inputRef.current; // latest snapshot from state

    const configOverrides = {
      ...current,
      id: current.id || null,
      message,
      options: {
        ...current.options,
        user: userProfile?.user_id ?? null,
        messages: updatedMessages,
        tool_choice,
      },
      user_id: userProfile?.user_id ?? null,
      _sessionKey: sessionKeyRef.current,
    };

    // Build config from overrides
    let config = generateConfig(configOverrides);

    // Force latest model/temperature/max_length into payload
    const latest = inputRef.current;
    config = {
      ...config,
      options: {
        ...config.options,
        model: latest?.options?.model,
        temperature: latest?.options?.temperature,
        max_length: latest?.options?.max_length,
      },
      settings: {
        ...config.settings,
        serviceType: latest?.settings?.serviceType,
        language: latest?.settings?.language,
        articleTone: latest?.settings?.articleTone,
      },
    };

    handleSendMessage(config);
  }, [userProfile?.user_id]);


  // Reset suggestions when service changes (small UX polish)
  useEffect(() => { setSuggestedPrompts([]); }, [input.settings.serviceType]);

  return (
    <div>
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Snackbar */}
        <Snackbar
          open={snackbarMessage.content !== ""}
          autoHideDuration={3000}
          onClose={() => setSnackbarMessage({ content: "", severity: "success" })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <div>
            <Alert onClose={() => setSnackbarMessage({ content: "", severity: "success" })} severity={snackbarMessage.severity}>
              {snackbarMessage.content}
            </Alert>
          </div>
        </Snackbar>
        <div className="ai-chat" style={{ height: "100%" }}>
          {/* Minimise Chat Selection */}
          <CircleIconButton color="primary" onClick={toggleFullscreenMode}>
            {aiChatSelectionVisible ? <RemoveIcon /> : <AddIcon />}
          </CircleIconButton>
          <Grid
            container
            spacing={2}
            sx={{ flex: 1, minHeight: 0 }}
          >
            {/* Chat Selection */}
            {aiChatSelectionVisible && (
              <Grid item sm={5} style={{ height: "100%" }}>
                <div data-testid="settings-component" style={{ marginBottom: "16px" }}>
                  <AiChatSelection
                    input={input}
                    setInput={setInput}
                    // If query params were applied, stop AdvancedOptions from hydrating from localStorage once
                    skipLocalStorageHydration={queryApplied}
                    // Pass down an offer for saving link settings to local defaults
                    linkHydrationOffer={linkHydrateRef.current}
                    // moved controls up here:
                    onShareSetup={handleShareSetup}
                    exposeParams={exposeParams}
                    setExposeParams={setExposeParams}
                  />
                </div>
                <AiPromptBar
                  onSend={handleUserSubmit}
                  language={input.settings.language}
                  variant="aiSettings"
                />
              </Grid>
            )}
            <Grid item sm={aiChatSelectionVisible ? 7 : 12}>
              <div>
                <div style={{ backgroundColor: "#343542", border: "1px solid #ccc", borderRadius: "4px" }}>
                  {/* Chat Box */}
                  <Box id="chat-box" ref={chatBoxRef} className="chat-box" style={{ minHeight: "300px", overflow: "auto", height: `calc(100vh - 259px)`, position: "relative" }}>
                    {showWatermark && !aiChatSelectionVisible && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: "16px" }}>
                        {/* Left: Model Selector */}
                        <ModelSelector currentModel={input.options.model} models={models} onModelChange={handleModelChange} />
                        {/* Right: Service Selector */}
                        <ServiceSelector currentService={input.settings.serviceType} services={serviceTypes} onServiceChange={handleServiceChange} />
                      </Box>
                    )}
                    {/* Mantis Watermark */}
                    {showWatermark && (
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none", zIndex: 1, color: "rgb(84 85 103)", fontSize: "24px", fontWeight: "bold", fontFamily: "Arial, sans-serif", textAlign: "center", textTransform: "uppercase", letterSpacing: "4px" }}>
                        Mantis Scribe Chat
                      </div>
                    )}
                    {showWatermark && (
                      <Box style={{ position: "absolute", top: "60%", left: "50%", transform: "translate(-50%, 0)", zIndex: 1, textAlign: "center", width: "100%" }}>
                        <SuggestedPrompts
                          title="Try one of these prompts"
                          prompts={serviceTypes.find((type) => type.value === input.settings.serviceType)?.prompts?.slice(0, 4) || []}
                          onSelectPrompt={(prompt) => handleUserSubmit({ message: prompt })}
                        />
                      </Box>
                    )}

                    {/* Render chat messages */}
                    {[...input.options.messages, loadingMessage].map((chat, index) => (
                      chat ? (
                        <div data-testid="ChatMessage-component" key={`${sessionKeyRef.current}-${index}`} ref={index === input.options.messages.length - 1 ? latestMessageRef : null}>
                          <ChatMessage chat={chat} chatConfig={input} index={index} handleAction={handleAction} />
                          {chat.role === "assistant" && index === input.options.messages.length - 1 && input.options.suggestionsEnabled && suggestedPrompts.length > 0 && (
                            <SuggestedPrompts prompts={suggestedPrompts} onSelectPrompt={(prompt) => handleUserSubmit({ message: prompt })} />
                          )}
                        </div>
                      ) : null
                    ))}
                  </Box>
                  {!aiChatSelectionVisible && (
                    <AiPromptBar onSend={handleUserSubmit} language={input.settings.language} mode='dark' variant="aiChat" />
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {/* Left cluster */}
                  <div>
                    <Button endIcon={<AddIcon />} variant="contained" style={{ backgroundColor: "#24ADE4", marginRight: "8px" }} onClick={handleStartNewChat}>
                      New Chat
                    </Button>
                  </div>

                  {/* Right cluster */}
                  <div>
                    {/* Regenerate Chat button */}
                    {showRegenerate && (
                      <Button endIcon={<ReplayIcon />} variant="outlined" color="primary" onClick={handleRegenerateResponse}>
                        Regenerate
                      </Button>
                    )}
                  </div>
                </div>
                <Box mt={1.2} bgcolor="#f8f8f8" borderRadius={4} fontSize={12} textAlign="center">
                  <Typography variant="body2" color="textSecondary" title="Mantis Scribe provides information based on available data. While we strive to maintain accuracy, please note that Mantis Scribe's responses may occasionally contain inaccuracies regarding people, places, or facts. It is recommended to independently verify important details before relying on the information provided. Mantis Scribe is intended for assistance purposes and should not replace professional advice or thorough research.">
                    <em>Mantis Scribe can make mistakes. Verify important details.</em>
                  </Typography>
                </Box>
              </div>
            </Grid>
          </Grid>
        </div>
      </Container>
    </div>
  );
};

export default AiChat;
